import React from 'react'
import { Button, Flex, Popconfirm, Select, Table, TableProps } from 'antd'
import { useTranslation } from 'react-i18next'
import { DeleteOutlined } from '@ant-design/icons'
import { CompanyUser } from '../../company/company-user'
import { UserAvatar } from './user-avatar'
import { useDeleteCompanyUser, useUpdateCompanyUserRole } from '../../company/hooks'
import { useAuth } from '../../auth'
import { DELETE_COMPANY_USER, SHOW_USER, UPDATE_COMPANY_USER_ROLE } from '../permission'
import { MailLink } from '../../components/mail-link'
import { CompanyRole, companyRoles } from '../../company/company-role'
import { Link } from '@tanstack/react-router'

export function ColleagueTable(props: TableProps<CompanyUser>) {
    const { t } = useTranslation()
    const { can } = useAuth()

    return (
        <Table
            size="small"
            columns={[
                {
                    title: '',
                    dataIndex: 'avatar',
                    width: 60,
                    render(text) {
                        return <UserAvatar user={{ avatar: text }} />
                    },
                },
                {
                    title: t('Name'),
                    dataIndex: 'name',
                    render: (name, record) =>
                        can(SHOW_USER) ? (
                            <Link
                                to="/users/$userId"
                                params={{ userId: record.userId }}
                            >
                                {name}
                            </Link>
                        ) : (
                            name
                        ),
                },
                { title: t('Email'), dataIndex: 'email', render: (email) => <MailLink email={email} /> },
                {
                    title: t('Role'),
                    dataIndex: 'role',
                    width: 150,
                    render(role, record) {
                        if (!can(UPDATE_COMPANY_USER_ROLE)) {
                            return t(`companyUserRole.${role}`)
                        }

                        return (
                            <RoleSelect
                                companyId={record.companyId}
                                userId={record.userId}
                                value={role}
                            />
                        )
                    },
                },
                {
                    key: 'action',
                    width: 50,
                    hidden: !can(DELETE_COMPANY_USER),
                    render: (_, record) => {
                        return (
                            <Flex justify="end">
                                <DeleteUserButton
                                    companyId={record.companyId}
                                    userId={record.userId}
                                />
                            </Flex>
                        )
                    },
                },
            ]}
            {...props}
        />
    )
}

type RoleSelectProps = {
    companyId: string
    userId: string
    value: CompanyRole
}

function RoleSelect({ companyId, userId, value }: RoleSelectProps) {
    const { t } = useTranslation()
    const { user, can } = useAuth()
    const updateRoleMutation = useUpdateCompanyUserRole(companyId, userId)

    const isOwnRole = user?.id === userId
    const canUpdate = can(UPDATE_COMPANY_USER_ROLE)
    const isDisabled = isOwnRole || !canUpdate || updateRoleMutation.isPending

    const handleChange = (newRole: CompanyRole) => {
        updateRoleMutation.mutate(newRole)
    }

    return (
        <Select
            value={value}
            onChange={handleChange}
            disabled={isDisabled}
            loading={updateRoleMutation.isPending}
            size="small"
            style={{ width: '100%' }}
            options={companyRoles.map((role) => ({ value: role, label: t(`companyUserRole.${role}`) }))}
        />
    )
}

function DeleteUserButton({ companyId, userId }: { companyId: string; userId: string }) {
    const deleteUserMutation = useDeleteCompanyUser(companyId, userId)
    const { t } = useTranslation()
    const { user } = useAuth()

    return (
        <Popconfirm
            title={t('Are you sure?')}
            onConfirm={() => deleteUserMutation.mutate()}
        >
            <Button
                type="text"
                disabled={user?.id === userId}
                loading={deleteUserMutation.isPending}
                icon={<DeleteOutlined />}
            />
        </Popconfirm>
    )
}
